import { Component } from '@theme/component';

/** Requests Liquid-rendered variant state without committing optimistic selections. */
class WishlistItemCard extends Component {
  async addToCart(event) {
    event.preventDefault();
    const form = event.composedPath()[0];
    if (!(form instanceof HTMLFormElement)) return;

    const button = form.querySelector('.wishlist-item-card__add');
    const variantId = new FormData(form).get('id');
    const drawer = document.querySelector('cart-drawer-component');
    if (!variantId || button?.disabled || this.getAttribute('aria-busy') === 'true') return;
    if (!drawer || typeof drawer.addVariant !== 'function') return;

    await drawer.addVariant(variantId, 1, button, true);
  }

  disconnectedCallback() {
    this.renderController?.abort();
    super.disconnectedCallback();
  }

  async changeVariant(event) {
    const selector = event.composedPath()[0].closest('[data-option-index]');
    if (!selector || !this.dataset.productUrl) return;

    const variantId = event.detail?.variantId ?? selector.selectedOptions?.[0]?.dataset.variantId;
    // Native selects commit before firing change. Restore the server's selection.
    if (selector instanceof HTMLSelectElement) {
      selector.selectedIndex = [...selector.options].findIndex((option) => option.defaultSelected);
    }
    if (!variantId) return;
    this.renderController?.abort();
    const controller = new AbortController();
    this.renderController = controller;
    this.setAttribute('aria-busy', 'true');
    const button = this.querySelector('.wishlist-item-card__add');
    // Retain the original server state across overlapping requests.
    this.serverButtonDisabled ??= button.disabled;
    button.disabled = true;

    const url = new URL(this.dataset.productUrl, window.location.origin);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', 'wishlist-card');

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`Wishlist card render failed (${response.status})`);
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const card = parsed.querySelector('wishlist-item-card[data-product-id]');
      if (!card || card.dataset.productId !== this.dataset.productId) {
        throw new Error('Wishlist card render returned the wrong product');
      }
      if (!this.isConnected || controller.signal.aborted) return;

      const focused = this.contains(document.activeElement);
      const imported = document.importNode(card, true);
      imported.querySelectorAll('script').forEach((script) => script.remove());
      this.replaceWith(imported);
      if (focused) {
        imported.querySelector(`option-selector-component[data-option-index="${selector.dataset.optionIndex}"] .option-selector__trigger, select[data-option-index="${selector.dataset.optionIndex}"]`)?.focus({ preventScroll: true });
      }
    } catch (error) {
      if (!controller.signal.aborted) console.error('Could not refresh wishlist card', error);
    } finally {
      if (this.isConnected && this.renderController === controller) {
        this.removeAttribute('aria-busy');
        button.disabled = this.serverButtonDisabled;
      }
    }
  }
}

if (!customElements.get('wishlist-item-card')) {
  customElements.define('wishlist-item-card', WishlistItemCard);
}
